import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { loadPdf, renderPageToCanvas } from '../lib/pdf';

export type PageSize = { width: number; height: number; rotation?: number };

/**
 * Renders a PDF page by page, width-driven.
 *
 * The page box is always 100% of its container with an aspect ratio locked to
 * the PDF's own geometry. Overlay children are positioned in percentages of
 * that box, so a field placed on a desktop lands in the same place on a phone.
 */
export function PdfView({
  url, pageSizes, renderOverlay, maxWidth = 920, onPageWidth, headers,
}: {
  url: string;
  pageSizes: PageSize[];
  renderOverlay?: (pageIndex: number, size: PageSize) => ReactNode;
  maxWidth?: number;
  onPageWidth?: (width: number) => void;
  /** Sent with the document request — used for the recipient signing session. */
  headers?: Record<string, string>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const [width, setWidth] = useState(0);

  // Track the rendered width so canvases re-render crisply after a resize or
  // device rotation.
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0].contentRect.width);
      setWidth((current) => (Math.abs(current - next) > 2 ? next : current));
    });
    observer.observe(node);
    setWidth(Math.floor(node.getBoundingClientRect().width));
    return () => observer.disconnect();
  }, []);

  useEffect(() => { if (width) onPageWidth?.(width); }, [width, onPageWidth]);

  useEffect(() => {
    let cancelled = false;
    if (!width) return;
    (async () => {
      try {
        const pdf = await loadPdf(url, headers);
        if (cancelled) return;
        const target = Math.min(width, maxWidth);
        for (let index = 0; index < pdf.numPages; index += 1) {
          const canvas = canvasRefs.current[index];
          if (!canvas) continue;
          await renderPageToCanvas(pdf, index + 1, canvas, target);
          if (cancelled) return;
        }
        setReady(true);
        setError(null);
      } catch (cause) {
        if (!cancelled) {
          setError('This document could not be displayed. Try reloading the page.');
          console.error(cause);
        }
      }
    })();
    return () => { cancelled = true; };
    // `headers` is intentionally not a dependency: its identity changes on every
    // render, and the session value is already part of the cache key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, width, maxWidth]);

  return (
    <div className="viewer" ref={containerRef}>
      {error && (
        <div className="banner banner--danger" role="alert">
          <span className="banner__icon" aria-hidden="true">⚠</span>
          <div>{error}</div>
        </div>
      )}
      {pageSizes.map((size, index) => (
        <div className="pdfpage" key={index}
             style={{ aspectRatio: `${size.width} / ${size.height}` }}>
          <canvas
            ref={(node) => { canvasRefs.current[index] = node; }}
            className="pdfpage__canvas"
            aria-label={`Page ${index + 1} of ${pageSizes.length}`}
            role="img"
          />
          {pageSizes.length > 1 && (
            <span className="pdfpage__label">{index + 1} / {pageSizes.length}</span>
          )}
          {renderOverlay && (
            <div className="pdfpage__layer">{renderOverlay(index, size)}</div>
          )}
        </div>
      ))}
      {!ready && !error && (
        <div className="loading-block" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>Rendering document…</span>
        </div>
      )}
    </div>
  );
}

/** Convert PDF-point geometry into CSS percentages of the page box. */
export function boxStyle(
  field: { x: number; y: number; width: number; height: number },
  size: PageSize,
): React.CSSProperties {
  return {
    left: `${(field.x / size.width) * 100}%`,
    // PDF origin is bottom-left; CSS is top-left.
    top: `${(1 - (field.y + field.height) / size.height) * 100}%`,
    width: `${(field.width / size.width) * 100}%`,
    height: `${(field.height / size.height) * 100}%`,
  };
}
